import { ApiClientError, isStepUpCancelled, referenceIdOf, referenceIdToShow } from "@/lib/api";
import { logger } from "@/lib/logger";
import { currentSurfaceKey } from "@/lib/surfaceKey";

/**
 * One failure, described the way every feedback surface shows it (ux-s10.md §3,
 * API-STANDARD.md §1 and §4).
 *
 * The page notice and the inline error bar print the same sentence, so the
 * description is built once here: the server's `message` (always safe to show),
 * the reference id support looks the failure up by, and whether trying again is
 * the right next step. A screen never builds this by hand, because a second copy
 * is how one surface ends up printing the reference id twice and another not at
 * all.
 */
export interface DescribedFailure {
  /** The sentence to show: the server's message, or the caller's fallback. */
  message: string;
  /** The correlation id to copy, or null for a failure that never reached the API. */
  reference: string | null;
  /**
   * The id to PRINT after `message`, or null when `message` already names it
   * (the non-envelope fallback in `lib/api.ts` writes the id into its sentence).
   */
  printedReference: string | null;
  /** The server said the same request may succeed if tried again. */
  retryable: boolean;
  /**
   * The server's per-field reasons (`error.details`), one line each, "field: issue" (B-23, round
   * 9). A screen that maps a field to its control shows it there; every generic surface shows
   * these lines under the sentence, so "Check the highlighted fields" never stands alone when
   * nothing is highlighted.
   */
  details?: string[];
  /**
   * #1110 review: the person cancelled a step-up sign-in. Nothing failed: the surfaces show the
   * sentence as a warning, with no failure title and no reference id.
   */
  cancelled?: boolean;
}

/**
 * Fields that are only an internal id of the thing the sentence is about, never a label (#1286;
 * `scheduleId` #1673: a schedule's Run now refusal read "scheduleId: Alpha is disabled.", and its
 * siblings on the same start, `dbtJobIds` ("Orders is disabled.") and `fullRefreshDataflowIds`).
 */
const INTERNAL_ID_FIELDS = new Set([
  "dataflowId",
  "scheduleId",
  "dbtJobIds",
  "fullRefreshDataflowIds",
]);

/**
 * One `details` entry as a line a person reads: "field: issue", so two fields with one reason stay
 * apart (B-23). Only an internal id (`INTERNAL_ID_FIELDS`) is left off, since the sentence already names
 * the thing (#1286).
 */
function detailLine(detail: { field?: string; issue: string }): string {
  const field = detail.field?.trim();
  return field && !INTERNAL_ID_FIELDS.has(field) ? `${field}: ${detail.issue}` : detail.issue;
}

/** The lines to show: none that repeat the sentence above them, and no exact line twice (#1286). */
function detailLines(message: string, details: { field?: string; issue: string }[]): string[] {
  const seen = new Set<string>();
  const lines: string[] = [];
  for (const detail of details) {
    if (detail.issue.trim() === message.trim()) continue;
    const line = detailLine(detail);
    if (seen.has(line.trim())) continue;
    seen.add(line.trim());
    lines.push(line);
  }
  return lines;
}

/** The title a retryable failure carries, as the toast path always showed it. */
export const RETRYABLE_TITLE = "Try again";

export function describeFailure(
  cause: unknown,
  fallbackMessage = "Something went wrong.",
): DescribedFailure {
  if (cause instanceof ApiClientError) {
    const reference = referenceIdOf(cause);
    return {
      message: cause.message,
      reference,
      printedReference: referenceIdToShow(cause.message, reference),
      retryable: cause.retryable,
      details: detailLines(cause.message, cause.details),
      ...(isStepUpCancelled(cause) ? { cancelled: true } : {}),
    };
  }
  return {
    message: fallbackMessage,
    reference: null,
    printedReference: null,
    retryable: false,
    details: [],
  };
}

/**
 * The sentence with its reference id on the end, exactly once: what the notice
 * shows as its message and what its live line announces. This is the `content`
 * fix (use-api-error-toast.ts:27-30): the toast put the id in `content`, which
 * the library renders INSTEAD of the message, so the server's sentence was lost.
 */
export function failureSentence(failure: DescribedFailure): string {
  const details = failure.details?.length ? ` (${failure.details.join(" · ")})` : "";
  return failure.printedReference
    ? `${failure.message}${details} Reference id ${failure.printedReference}.`
    : `${failure.message}${details}`;
}

/**
 * Record that a failure was put in front of the user, identifiers only (SEC-17):
 * the error code, the HTTP status, the correlation id and the surface key. Never
 * the message, which can carry what the user typed.
 */
export function reportFailureShown(cause: unknown): void {
  if (cause instanceof ApiClientError) {
    logger.warn("api error shown to user", {
      "skydata.error_code": cause.code,
      "skydata.status": cause.status,
      "skydata.correlation_id": cause.correlationId,
      "skydata.surface": currentSurfaceKey(),
    });
    return;
  }
  logger.error("unexpected error shown to user", cause);
}

/**
 * Copy a reference id, best effort. An insecure origin or a denied permission
 * leaves the id on screen to be typed, which is why it is never ONLY behind the
 * button. Resolves to whether the copy went through.
 */
export async function copyReference(reference: string): Promise<boolean> {
  try {
    if (!navigator.clipboard) return false;
    await navigator.clipboard.writeText(reference);
    return true;
  } catch {
    return false;
  }
}
